/**
 * Stepped Product Options
 * Manages step-by-step accordion behavior for variant and subscription selection.
 * All steps are siblings - variants first, then subscription as the last step.
 */

if (!customElements.get('stepped-product-options')) {
  customElements.define(
    'stepped-product-options',
    class SteppedProductOptions extends HTMLElement {
      constructor() {
        super();
        this.accordions = [];
        this.currentStep = 1;
        this.totalSteps = 0;
        this.sectionId = '';
        this.totalVariantSteps = 0;
        this.completedSteps = new Set();
        this.pleaseCompleteFirst = '';
      }

      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.totalVariantSteps = parseInt(this.dataset.totalVariantSteps, 10) || 0;
        this.pleaseCompleteFirst = this.dataset.pleaseCompleteFirst || 'Please complete "[STEP]" first';
        
        // Add class to section IMMEDIATELY to hide price/buttons before anything renders
        const section = document.querySelector(`#shopify-section-${this.sectionId}`);
        if (section) {
          section.classList.add('section-has-stepped-options');
        }
        
        // Show loader and hide content initially
        this.showLoader();
        
        // Check for variant in URL
        const urlVariantId = this.getVariantIdFromUrl();
        
        // Initialize after a short delay to allow DOM to settle
        setTimeout(() => {
          this.initializeAccordions();
          
          // Get variant data before clearing (if URL has variant param)
          let urlVariantData = null;
          if (urlVariantId) {
            urlVariantData = this.getVariantData(urlVariantId);
          }
          
          this.clearPreSelectedOptions();
          
          // If there's a variant in the URL, pre-select it
          if (urlVariantId && urlVariantData) {
            this.preSelectVariantFromUrl(urlVariantId, urlVariantData);
          } else if (urlVariantId) {
            // Fallback: try to match by option values
            this.preSelectVariantByMatchingOptions(urlVariantId);
          }
          
          this.setupEventListeners();
          
          this.hideLoader();
        }, 200); // Allow DOM to settle before initializing
      }

      /**
       * Initialize accordion elements
       */
      initializeAccordions() {
        // Get all accordion steps (both variant and subscription)
        this.accordions = Array.from(this.querySelectorAll('.stepped-accordion'));
        this.totalSteps = this.accordions.length;

        // Initialize step states
        this.accordions.forEach((accordion, index) => {
          const stepNumber = index + 1;
          const details = accordion.querySelector('details');
          
          // Only first step is initially open and active
          if (stepNumber === 1) {
            accordion.classList.add('stepped-accordion--active');
            accordion.classList.remove('stepped-accordion--locked', 'stepped-accordion--completed');
            if (details) details.setAttribute('open', '');
          } else {
            accordion.classList.add('stepped-accordion--locked');
            accordion.classList.remove('stepped-accordion--active', 'stepped-accordion--completed');
            if (details) details.removeAttribute('open');
          }
        });
      }

      /**
       * Show loader to hide content during initialization
       */
      showLoader() {
        this.style.position = 'relative';
        this.style.minHeight = '200px';
        
        // Create loader element if it doesn't exist
        if (!this.querySelector('.stepped-options-loader')) {
          const loader = document.createElement('div');
          loader.className = 'stepped-options-loader';
          loader.innerHTML = `
            <div class="stepped-options-loader__spinner"></div>
          `;
          // Append to the element (will be on top due to absolute positioning)
          this.appendChild(loader);
        }
        
        // Accordions are already hidden via CSS, no need to hide them again
      }


      /**
       * Hide loader and show content
       */
      hideLoader() {
        const loader = this.querySelector('.stepped-options-loader');
        if (loader) {
          loader.remove();
        }
        
        // Mark as initialized to show accordions via CSS
        this.classList.add('initialized');
        
        // Mark section as initialized to show price/buttons via CSS
        const section = document.querySelector(`#shopify-section-${this.sectionId}`);
        if (section) {
          section.classList.add('stepped-options-initialized');
        }
        
        // Update button state (will be disabled since no selections made)
        this.updateAddToCartButton();
        
        this.style.minHeight = '';
      }

      /**
       * Get variant ID from URL parameters
       * @returns {string|null} Variant ID if found in URL, null otherwise
       */
      getVariantIdFromUrl() {
        const urlParams = new URLSearchParams(window.location.search);
        const variantId = urlParams.get('variant');
        return variantId || null;
      }

      /**
       * Get variant data from the variant-selects element
       * @param {string} variantId - The variant ID to find
       * @returns {Object|null} Variant object if found, null otherwise
       */
      getVariantData(variantId) {
        // Try to get variant from the variant-selects element
        const variantSelects = this.querySelector('variant-selects');
        if (!variantSelects) return null;

        // Check if Shopify has already selected the variant (which it should if URL has variant param)
        const variantScript = variantSelects.querySelector('script[data-selected-variant]');
        if (variantScript) {
          try {
            const selectedVariant = JSON.parse(variantScript.textContent);
            // If the selected variant matches the URL variant, use it
            if (selectedVariant.id && selectedVariant.id.toString() === variantId.toString()) {
              return selectedVariant;
            }
          } catch (e) {
            console.warn('Could not parse variant data:', e);
          }
        }

        return null;
      }

      /**
       * Pre-select variant options based on URL variant parameter
       * @param {string} variantId - The variant ID from the URL
       * @param {Object} variant - The variant data object
       */
      preSelectVariantFromUrl(variantId, variant) {
        if (!variant || !variant.options || variant.options.length === 0) {
          // If we can't get variant data directly, try to find it by matching option values
          this.preSelectVariantByMatchingOptions(variantId);
          return;
        }

        // Get only variant accordions (exclude subscription)
        const variantAccordions = this.accordions.filter(acc => 
          !acc.classList.contains('stepped-accordion--subscription')
        );

        // When loading with a variant in URL, assume ALL variant steps are completed
        // We can't know which specific steps were completed, so mark them all
        variantAccordions.forEach((accordion) => {
          const stepNumber = this.accordions.indexOf(accordion) + 1;
          this.completedSteps.add(stepNumber);
        });

        // Pre-select each option value sequentially to allow availability updates
        this.preSelectVariantOptionsSequentially(variant.options, variantAccordions).then(() => {
          // Update selection displays and mark all variant steps as completed
          variantAccordions.forEach((accordion, index) => {
            const stepNumber = this.accordions.indexOf(accordion) + 1;
            const optionValue = variant.options[index];
            
            if (optionValue) {
              this.updateSelectionDisplay(accordion, optionValue, true);
              this.markStepCompleted(accordion, stepNumber, false); // Don't unlock next (we'll handle that)
            }
            
            // Close all completed variant steps
            this.closeStep(stepNumber);
          });

          // Update button state and open the next incomplete step
          this.updateAddToCartButton();
          
          // Find the first incomplete step (should be subscription if it exists)
          let firstIncompleteStep = null;
          for (let i = 0; i < this.accordions.length; i++) {
            if (!this.completedSteps.has(i + 1)) {
              firstIncompleteStep = i + 1;
              break;
            }
          }
          
          if (firstIncompleteStep) {
            this.openStep(firstIncompleteStep);
            this.currentStep = firstIncompleteStep;
          }
        });
      }

      /**
       * Sync disabled states from server-rendered HTML to accordion inputs
       * This is called when variantChange event fires with the fetched HTML
       * @param {Document} html - The parsed HTML document from the server
       */
      syncAccordionInputsFromServerHtml(html) {
        // Get the stepped-product-options element from the server HTML
        const serverSteppedOptions = html.querySelector('stepped-product-options');
        if (!serverSteppedOptions) return;

        // Get all variant accordions from server HTML
        const serverAccordions = serverSteppedOptions.querySelectorAll('.stepped-accordion--variant');
        
        // Get our variant accordions (exclude subscription)
        const variantAccordions = this.accordions.filter(acc => 
          !acc.classList.contains('stepped-accordion--subscription')
        );

        variantAccordions.forEach((accordion, accordionIndex) => {
          const serverAccordion = serverAccordions[accordionIndex];
          if (!serverAccordion) return;

          // Get all inputs from server accordion
          const serverInputs = serverAccordion.querySelectorAll('input[type="radio"], select option');
          
          // Create a map of server input states by value
          const serverInputStates = new Map();
          serverInputs.forEach((input) => {
            const value = input.value;
            const isDisabled = input.disabled || 
                              input.classList.contains('disabled') || 
                              input.hasAttribute('disabled');
            const isSoldOut = input.classList.contains('sold-out');
            const isUnavailable = input.classList.contains('unavailable');
            
            serverInputStates.set(value, {
              disabled: isDisabled,
              soldOut: isSoldOut,
              unavailable: isUnavailable
            });
          });

          // Get our accordion inputs and apply states
          const accordionInputs = accordion.querySelectorAll('input[type="radio"], select option');
          accordionInputs.forEach((input) => {
            const value = input.value;
            const serverState = serverInputStates.get(value);
            
            if (serverState) {
              if (input.tagName === 'OPTION') {
                if (serverState.disabled) {
                  input.setAttribute('disabled', 'disabled');
                  input.disabled = true;
                } else {
                  input.removeAttribute('disabled');
                  input.disabled = false;
                }
              } else {
                // Radio button
                if (serverState.disabled) {
                  input.classList.add('disabled');
                  input.setAttribute('disabled', 'disabled');
                  input.disabled = true;
                } else {
                  input.classList.remove('disabled');
                  input.removeAttribute('disabled');
                  input.disabled = false;
                }
                
                // Also sync sold-out and unavailable classes
                if (serverState.soldOut) {
                  input.classList.add('sold-out');
                } else {
                  input.classList.remove('sold-out');
                }
                
                if (serverState.unavailable) {
                  input.classList.add('unavailable');
                } else {
                  input.classList.remove('unavailable');
                }
              }
              
              // Also update the label if it has unavailable text
              const label = accordion.querySelector(`label[for="${input.id}"]`);
              if (label) {
                const unavailableSpan = label.querySelector('.label-unavailable');
                if (unavailableSpan) {
                  if (serverState.soldOut) {
                    unavailableSpan.textContent = ' (Sold out)';
                    unavailableSpan.style.display = '';
                  } else if (serverState.unavailable) {
                    unavailableSpan.textContent = ' (Unavailable)';
                    unavailableSpan.style.display = '';
                  } else {
                    unavailableSpan.textContent = '';
                    unavailableSpan.style.display = 'none';
                  }
                }
              }
            }
          });
        });
      }

      /**
       * Clear any auto-selected defaults in an accordion
       * This prevents Shopify from auto-selecting defaults when we're pre-selecting from URL
       * @param {HTMLElement} accordion - The accordion to clear
       */
      clearAutoSelectedDefaults(accordion) {
        // Clear radio button selections
        const checkedRadios = accordion.querySelectorAll('input[type="radio"]:checked');
        checkedRadios.forEach((radio) => {
          // Only clear if it's not the one we want to select
          // We'll check this by seeing if it matches our target value later
          radio.checked = false;
          radio.removeAttribute('checked');
        });

        // Reset select dropdowns to first option (disabled placeholder)
        const selects = accordion.querySelectorAll('select');
        selects.forEach((select) => {
          // Only reset if it's not already at index 0
          if (select.selectedIndex > 0) {
            select.selectedIndex = 0;
            // Remove selected attribute from all options
            Array.from(select.options).forEach((option) => {
              option.removeAttribute('selected');
              option.selected = false;
            });
          }
        });
      }

      /**
       * Pre-select variant options sequentially to allow availability updates
       * @param {Array} optionValues - Array of option values to select
       * @param {Array} variantAccordions - Array of variant accordion elements
       * @returns {Promise} Promise that resolves when all selections are complete
       */
      preSelectVariantOptionsSequentially(optionValues, variantAccordions) {
        return new Promise((resolve) => {
          let currentIndex = 0;

          const selectNext = async () => {
            if (currentIndex >= optionValues.length) {
              resolve();
              return;
            }

            const optionValue = optionValues[currentIndex];
            const accordion = variantAccordions[currentIndex];
            
            if (!accordion) {
              currentIndex++;
              selectNext();
              return;
            }

            // Find the input/option that matches this value
            // Only select if it's not disabled
            const allInputs = accordion.querySelectorAll('input[type="radio"], select option');
            let found = false;
            
            for (const input of allInputs) {
              // Skip disabled options
              if (input.disabled || input.classList.contains('disabled') || 
                  (input.tagName === 'OPTION' && input.hasAttribute('disabled'))) {
                continue;
              }

              const value = input.tagName === 'OPTION' ? input.value : input.value;
              
              // Match by exact value (case-insensitive, trimmed)
              if (value.trim().toLowerCase() === optionValue.trim().toLowerCase()) {
                found = true;
                
                if (input.tagName === 'OPTION') {
                  // For select options, set the select's selectedIndex
                  const select = input.closest('select');
                  if (select) {
                    const selectOptionIndex = Array.from(select.options).indexOf(input);
                    if (selectOptionIndex >= 0) {
                      select.selectedIndex = selectOptionIndex;
                      // Trigger change event to update UI and availability
                      select.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                  }
                } else {
                  // For radio buttons, check it
                  input.checked = true;
                  input.dispatchEvent(new Event('change', { bubbles: true }));
                }
                
                // Update the selection display
                const actualStepNumber = this.accordions.indexOf(accordion) + 1;
                this.updateSelectionDisplay(accordion, optionValue, true);
                this.markStepCompleted(accordion, actualStepNumber, true);
                this.completedSteps.add(actualStepNumber);
                
                break;
              }
            }

            currentIndex++;
            
            if (found) {
              // Give Shopify a moment to update availability, but don't wait too long
              // Shopify will handle the updates automatically
              setTimeout(() => {
                // Clear any auto-selected defaults in the next accordion before we select
                const nextAccordion = currentIndex < variantAccordions.length ? variantAccordions[currentIndex] : null;
                if (nextAccordion && currentIndex < optionValues.length) {
                  this.clearAutoSelectedDefaults(nextAccordion);
                }
                selectNext();
              }, 200); // Reduced delay - let Shopify handle updates in background
            } else {
              // If not found, continue immediately
              selectNext();
            }
          };

          selectNext();
        });
      }

      /**
       * Pre-select inputs sequentially to allow availability updates
       * @param {Array} selections - Array of {accordion, input, stepNumber} objects
       * @returns {Promise} Promise that resolves when all selections are complete
       */
      preSelectInputsSequentially(selections) {
        return new Promise((resolve) => {
          let currentIndex = 0;

          const selectNext = () => {
            if (currentIndex >= selections.length) {
              resolve();
              return;
            }

            const { accordion, input, stepNumber } = selections[currentIndex];
            
            if (input.tagName === 'OPTION') {
              const select = input.closest('select');
              if (select) {
                const selectOptionIndex = Array.from(select.options).indexOf(input);
                if (selectOptionIndex >= 0) {
                  select.selectedIndex = selectOptionIndex;
                  select.dispatchEvent(new Event('change', { bubbles: true }));
                }
              }
            } else {
              input.checked = true;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
            
            const value = input.tagName === 'OPTION' ? input.value : input.value;
            this.updateSelectionDisplay(accordion, value, true);
            this.markStepCompleted(accordion, stepNumber, true);
            this.completedSteps.add(stepNumber);
            
            // Wait for the change event to be processed and DOM to update
            currentIndex++;
            setTimeout(() => {
              selectNext();
            }, 300); // Give time for async updates to complete
          };

          selectNext();
        });
      }

      /**
       * Pre-select variant by matching option values from inputs
       * This is a fallback when we can't get variant data directly
       * @param {string} variantId - The variant ID from the URL
       */
      preSelectVariantByMatchingOptions(variantId) {
        // Get all variant option inputs and try to find which combination matches the variant
        // We can check data-product-url on inputs to see if they lead to this variant
        const variantSelects = this.querySelector('variant-selects');
        if (!variantSelects) return;

        // Get the product URL to construct variant URLs
        const productInfo = this.closest('product-info');
        const productUrl = productInfo?.dataset.url || window.location.pathname;

        // Get variant accordions
        const variantAccordions = this.accordions.filter(acc => 
          !acc.classList.contains('stepped-accordion--subscription')
        );

        // Find matching inputs for each accordion
        const selectionsToMake = [];
        variantAccordions.forEach((accordion, accordionIndex) => {
          const allInputs = accordion.querySelectorAll('input[type="radio"], select option');
          for (const input of allInputs) {
            const productUrlAttr = input.dataset.productUrl;
            if (productUrlAttr && productUrlAttr.includes(`variant=${variantId}`)) {
              selectionsToMake.push({
                accordion,
                input,
                stepNumber: accordionIndex + 1
              });
              break; // Only take first match per accordion
            }
          }
        });

        // Select sequentially
        this.preSelectInputsSequentially(selectionsToMake).then(() => {
          // Close all completed variant steps
          variantAccordions.forEach((accordion) => {
            const stepNumber = this.accordions.indexOf(accordion) + 1;
            if (this.completedSteps.has(stepNumber)) {
              this.closeStep(stepNumber);
            }
          });

          // Update button state and open the next incomplete step
          this.updateAddToCartButton();
          
          // Find the first incomplete step and open it
          let firstIncompleteStep = null;
          for (let i = 0; i < this.accordions.length; i++) {
            if (!this.completedSteps.has(i + 1)) {
              firstIncompleteStep = i + 1;
              break;
            }
          }
          
          if (firstIncompleteStep) {
            this.openStep(firstIncompleteStep);
            this.currentStep = firstIncompleteStep;
          }
        });
      }

      /**
       * Clear all pre-selected options on page load
       * This prevents Shopify's auto-selected variant from appearing selected
       */
      clearPreSelectedOptions() {
        // Reset completed steps tracking
        this.completedSteps.clear();
        
        this.accordions.forEach((accordion) => {
          // Clear radio button selections
          const checkedRadios = accordion.querySelectorAll('input[type="radio"]:checked');
          checkedRadios.forEach((radio) => {
            radio.checked = false;
            radio.removeAttribute('checked');
          });

          // Reset select dropdowns (including subscription selects)
          const selects = accordion.querySelectorAll('select');
          selects.forEach((select) => {
            // Reset to first option (index 0)
            // For subscription selects, this will be the disabled placeholder option
            select.selectedIndex = 0;
            
            // Remove selected attribute from all options
            Array.from(select.options).forEach((option) => {
              option.removeAttribute('selected');
              option.selected = false;
            });
            
            // Clear dropdown swatch display if present
            const dropdownSwatch = select.closest('.product-form__input')?.querySelector('[data-selected-value]');
            if (dropdownSwatch) {
              dropdownSwatch.innerHTML = '';
            }
            
            // Don't trigger change event during initial clear to avoid side effects
          });

          // Clear selection display in accordion title
          const selectionDisplay = accordion.querySelector('[data-selection-display]');
          if (selectionDisplay) {
            selectionDisplay.innerHTML = '';
            selectionDisplay.style.display = 'none';
          }

          // Hide checkmark
          const checkmark = accordion.querySelector('.stepped-accordion__checkmark');
          if (checkmark) {
            checkmark.style.display = 'none';
          }
        });
      }

      /**
       * Get the existing selection value from an accordion
       */
      getExistingSelectionValue(accordion) {
        // Check for checked radio button (including subscription radios)
        const checkedRadio = accordion.querySelector('input[type="radio"]:checked');
        if (checkedRadio) {
          const label = accordion.querySelector(`label[for="${checkedRadio.id}"]`);
          if (label) {
            const labelClone = label.cloneNode(true);
            const hiddenElements = labelClone.querySelectorAll('.visually-hidden, .label-unavailable');
            hiddenElements.forEach(el => el.remove());
            return labelClone.textContent.trim();
          }
          return checkedRadio.value;
        }

        // Check for select element (fallback for hidden select)
        const select = accordion.querySelector('select');
        if (select && select.selectedIndex >= 0) {
          return select.options[select.selectedIndex].textContent.trim();
        }

        return '';
      }

      /**
       * Check if viewer information is required and complete for subscription steps
       */
      checkSubscriptionViewerInfoComplete(accordion) {
        const isSubscription = accordion.classList.contains('stepped-accordion--subscription');
        if (!isSubscription) return true; // Not a subscription step, no viewer info needed

        // Check for subscription radio button first
        const checkedRadio = accordion.querySelector('.subscription-radio:checked');
        if (!checkedRadio) return false;

        const selectedValue = checkedRadio.value;
        const label = accordion.querySelector(`label[for="${checkedRadio.id}"]`);
        const selectedText = label ? label.textContent.toLowerCase() : '';

        // Check if viewer information is required
        const isProSubscription = selectedText.includes('pro');
        const isLiteSubscription = selectedText.includes('lite');
        const isReseller = selectedValue === 'reseller';
        const isExisting = selectedValue === 'existing';
        const isActivateLater = selectedValue === 'activate_later';

        // If it's "existing" or other non-viewer options (except activate_later which requires acknowledgment), no viewer info needed
        if (isExisting || (!isProSubscription && !isLiteSubscription && !isReseller && !isActivateLater)) {
          return true;
        }

        // For activate_later, check if user has acknowledged by clicking "I Understand" button
        // The button container is hidden after acknowledgment
        if (isActivateLater) {
          const activateLaterDoneContainer = accordion.querySelector('[id^="subscription-done-container-activate-later"]');
          // Return true only if the container is hidden (user clicked the button)
          return activateLaterDoneContainer && activateLaterDoneContainer.style.display === 'none';
        }

        // For reseller, check customer details
        if (isReseller) {
          const customerNameInput = accordion.querySelector('#custom-input--customer_name');
          const customerEmailInput = accordion.querySelector('#custom-input--customer_email');
          
          if (customerNameInput && customerEmailInput) {
            const customerName = customerNameInput.value.trim();
            const customerEmail = customerEmailInput.value.trim();
            return customerName.length > 0 && customerEmail.length > 0;
          }
          return false;
        }

        // For PRO and LITE subscriptions, check viewer information
        const viewerDetailsContainer = accordion.querySelector('.viewer-details-container');
        if (!viewerDetailsContainer || viewerDetailsContainer.style.display === 'none') {
          return false; // Viewer container should be visible
        }

        // Get all viewer entries
        const viewerEntries = accordion.querySelectorAll('.viewer-entry');
        if (viewerEntries.length === 0) return false;

        // Check each viewer entry has name and email filled
        for (const entry of viewerEntries) {
          const nameInput = entry.querySelector('input[type="text"][name^="properties[Viewer"]');
          const emailInput = entry.querySelector('input[type="email"][name^="properties[Viewer"]');
          
          if (!nameInput || !emailInput) return false;
          
          const name = nameInput.value.trim();
          const email = emailInput.value.trim();
          
          if (name.length === 0 || email.length === 0) {
            return false;
          }
        }

        return true;
      }

      /**
       * Check if a step has a selection made
       */
      checkStepHasSelection(accordion) {
        // Check if this is a skipped step (like add-ons when subscription is "existing" or "reseller")
        if (accordion.classList.contains('stepped-accordion--skipped')) {
          return true;
        }

        // Check if this is an add-ons step - always valid (doesn't block add to cart)
        const isAddons = accordion.classList.contains('stepped-accordion--addons');
        if (isAddons) {
          return true; // Add-ons are optional, never block add to cart
        }

        // Check for radio buttons (including subscription radios)
        const checkedRadio = accordion.querySelector('input[type="radio"]:checked');
        if (checkedRadio) {
          // For subscription radios, also check if viewer information is complete (if required)
          const isSubscription = accordion.classList.contains('stepped-accordion--subscription');
          if (isSubscription && checkedRadio.classList.contains('subscription-radio')) {
            return this.checkSubscriptionViewerInfoComplete(accordion);
          }
          return true;
        }

        // Check for select elements (fallback for hidden select or other selects)
        const select = accordion.querySelector('select');
        if (select) {
          // For subscription selects, check if a valid option is selected (not disabled placeholder)
          const isSubscription = accordion.classList.contains('stepped-accordion--subscription');
          if (isSubscription) {
            // Subscription selects: check if selected option is not disabled AND viewer info is complete
            const selectedOption = select.options[select.selectedIndex];
            if (selectedOption && !selectedOption.disabled && select.selectedIndex > 0) {
              // Also check if viewer information is complete (if required)
              return this.checkSubscriptionViewerInfoComplete(accordion);
            }
          } else if (select.selectedIndex > 0) {
            return true;
          }
        }

        return false;
      }

      /**
       * Check if all steps are completed
       */
      areAllStepsCompleted() {
        // Check all accordions have valid selections
        for (let i = 0; i < this.accordions.length; i++) {
          const accordion = this.accordions[i];
          if (!this.checkStepHasSelection(accordion)) {
            return false;
          }
        }
        return true;
      }

      /**
       * Update add to cart button state based on completion
       * @returns {boolean} true if button was just enabled, false otherwise
       */
      updateAddToCartButton() {
        const section = document.querySelector(`#shopify-section-${this.sectionId}`);
        if (!section) return false;

        const submitButton = section.querySelector('.product-form__submit, #ProductSubmitButton-' + this.sectionId);
        if (!submitButton) return false;

        const allCompleted = this.areAllStepsCompleted();
        const wasDisabled = submitButton.hasAttribute('disabled') || submitButton.classList.contains('disabled');
        
        if (allCompleted) {
          submitButton.removeAttribute('disabled');
          submitButton.classList.remove('disabled');
          // Return true if button was just enabled (was disabled, now enabled)
          return wasDisabled;
        } else {
          submitButton.setAttribute('disabled', 'disabled');
          submitButton.classList.add('disabled');
          return false;
        }
      }

      /**
       * Scroll to the buy button to ensure it's visible
       */
      scrollToBuyButton() {
        const section = document.querySelector(`#shopify-section-${this.sectionId}`);
        if (!section) {
          return;
        }

        // Function to perform the actual scroll
        const performScroll = () => {
          // Try multiple selectors in order of preference
          const buyButtonContainer = section.querySelector('.buyButtons__block');
          const productForm = section.querySelector('.product-form');
          const submitButton = section.querySelector('.product-form__submit, #ProductSubmitButton-' + this.sectionId);
          const productFormButtons = section.querySelector('.product-form__buttons');
          
          // Prefer scrolling to the container, then form, then buttons div, then button itself
          const scrollTarget = buyButtonContainer || productForm || productFormButtons || submitButton;
          
          if (!scrollTarget) {
            return false;
          }

          // Get current scroll position and element position
          const rect = scrollTarget.getBoundingClientRect();
          const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
          const elementTop = rect.top + currentScrollY;
          const elementHeight = rect.height;
          const viewportHeight = window.innerHeight;
          
          // Calculate target scroll position to center the element
          // Use the element's current viewport position to calculate scroll
          const targetScrollY = currentScrollY + rect.top + (elementHeight / 2) - (viewportHeight / 2);
          
          // Check if element is already visible and reasonably centered
          const isVisible = rect.top >= 0 && rect.bottom <= viewportHeight;
          const isCentered = Math.abs(rect.top + (elementHeight / 2) - (viewportHeight / 2)) < 100;
          
          if (!isVisible || !isCentered) {
            // Use window.scrollTo for more reliable scrolling
            window.scrollTo({
              top: Math.max(0, targetScrollY),
              behavior: 'smooth'
            });
            
            // Verify scroll worked after animation
            setTimeout(() => {
              const newRect = scrollTarget.getBoundingClientRect();
              const newIsVisible = newRect.top >= 0 && newRect.bottom <= viewportHeight;
              if (!newIsVisible) {
                // Fallback: recalculate and use instant scroll if smooth didn't work
                const newScrollY = (window.pageYOffset || document.documentElement.scrollTop) + newRect.top + (newRect.height / 2) - (viewportHeight / 2);
                window.scrollTo({
                  top: Math.max(0, newScrollY),
                  behavior: 'auto'
                });
              }
            }, 600);
          }
          
          return true;
        };

        // Use requestAnimationFrame to ensure DOM has updated
        requestAnimationFrame(() => {
          if (!performScroll()) {
            // If not found, try again after a short delay
            setTimeout(() => {
              performScroll();
            }, 100);
          }
        });
      }

      /**
       * Setup event listeners for selections
       */
      setupEventListeners() {
        // Listen for changes on all accordions
        this.accordions.forEach((accordion, index) => {
          const stepNumber = index + 1;
          
          // Radio button changes
          accordion.addEventListener('change', (event) => {
            const target = event.target;
            
            if (target.id === 'suggested_subscription') {
              return;
            }
            
            if (target.type === 'radio' || target.tagName === 'SELECT') {
              this.handleSelection(accordion, stepNumber, target);
            }
          });

          // Prevent locked accordions from opening
          const summary = accordion.querySelector('summary');
          if (summary) {
            summary.addEventListener('click', (event) => {
              if (accordion.classList.contains('stepped-accordion--locked')) {
                event.preventDefault();
                this.showLockedMessage(accordion, stepNumber);
              }
            });
          }
        });

        // Also listen for variant selection changes from the variant-selects element
        const variantSelects = this.querySelector('variant-selects');
        if (variantSelects) {
          variantSelects.addEventListener('change', (event) => {
            // Variant selects handles its own logic, we just need to sync
            // Update button state when variant changes
            this.updateAddToCartButton();
          });
          
          // Subscribe to variantChange pub/sub event - this fires AFTER Shopify updates the DOM
          // This event includes the fetched HTML with updated availability states
          if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
              // The event includes the fetched HTML from the server
              const html = event.data?.html;
              if (html) {
                // Sync accordion inputs from the server HTML which has correct availability
                this.syncAccordionInputsFromServerHtml(html);
              }
            });
          }
        }

        // Listen for subscription changes (both radio buttons and hidden select)
        const subscriptionSelects = this.querySelectorAll('subscription-selects');
        subscriptionSelects.forEach((subscriptionSelect) => {
          // Listen for radio button changes
          const subscriptionRadios = subscriptionSelect.querySelectorAll('.subscription-radio');
          subscriptionRadios.forEach((radio) => {
            radio.addEventListener('change', (event) => {
              // Update button state when subscription changes
              this.updateAddToCartButton();
            });
          });
          
          // Also listen for hidden select changes (fallback)
          subscriptionSelect.addEventListener('change', (event) => {
            // Update button state when subscription changes
            this.updateAddToCartButton();
          });
        });

        // Listen for subscription step done button click
        this.accordions.forEach((accordion, index) => {
          const stepNumber = index + 1;
          const isSubscription = accordion.classList.contains('stepped-accordion--subscription');
          const isAddons = accordion.classList.contains('stepped-accordion--addons');
          
          if (isSubscription) {
            accordion.addEventListener('subscription-step-done', (event) => {
              // Check if viewer information is complete
              if (this.checkSubscriptionViewerInfoComplete(accordion)) {
                // Complete the step
                this.checkAndCompleteSubscriptionStep(accordion, stepNumber);
              } else {
                // Show validation message
                alert('Please fill in all required viewer information before completing this step.');
              }
            });
          }
          
        });
        
        // Listen for subscription type changes to show/hide add-ons step
        document.addEventListener('subscription-type-changed', (event) => {
          this.handleSubscriptionTypeChange(event.detail);
        });

        // Listen for clear button click
        const clearButton = this.closest('[id^="shopify-section-"]')?.querySelector('[data-clear-options]');
        if (clearButton) {
          clearButton.addEventListener('click', () => {
            // Remove variant parameter from URL and reload
            const url = new URL(window.location.href);
            url.searchParams.delete('variant');
            window.location.href = url.toString();
          });
        }
      }

      /**
       * Check if subscription step can be completed and mark it as such if all requirements are met
       */
      checkAndCompleteSubscriptionStep(accordion, stepNumber) {
        // Check if viewer information is now complete
        if (this.checkSubscriptionViewerInfoComplete(accordion)) {
          // Check if step is not already completed
          if (!this.completedSteps.has(stepNumber)) {
            // Get the current selection value to update display with checkmark
            const select = accordion.querySelector('select');
            const checkedRadio = accordion.querySelector('.subscription-radio:checked');
            let value = '';
            
            if (checkedRadio) {
              const label = accordion.querySelector(`label[for="${checkedRadio.id}"]`);
              value = label ? label.textContent.trim() : checkedRadio.value;
            } else if (select && select.selectedIndex > 0) {
              value = select.options[select.selectedIndex].textContent.trim();
            }
            
            // Update selection display with checkmark now that step is complete
            this.updateSelectionDisplay(accordion, value, true);
            
            // Mark this step as completed
            this.markStepCompleted(accordion, stepNumber, true);
            
            // Track completed step
            this.completedSteps.add(stepNumber);
            
            // Update add to cart button state
            this.updateAddToCartButton();
            
            // Check if all steps are now completed (this is the last step)
            const allStepsCompleted = this.areAllStepsCompleted();
            const isLastStep = stepNumber === this.totalSteps;
            
            // Close current accordion and open next after a short delay
            setTimeout(() => {
              this.closeStep(stepNumber);
              
              // Open next step if available and visible
              if (stepNumber < this.totalSteps) {
                const nextAccordion = this.accordions[stepNumber]; // stepNumber is already +1 index
                // Skip hidden/skipped steps
                if (nextAccordion && nextAccordion.style.display !== 'none' && !nextAccordion.classList.contains('stepped-accordion--skipped')) {
                  this.openStep(stepNumber + 1);
                  this.currentStep = stepNumber + 1;
                  
                  // Scroll to the next step (add-ons) to ensure it's visible
                  setTimeout(() => {
                    nextAccordion.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                } else if (allStepsCompleted) {
                  // Next step is hidden but all steps complete - scroll to buy button
                  setTimeout(() => {
                    requestAnimationFrame(() => {
                      this.scrollToBuyButton();
                    });
                  }, 300);
                }
              } else if (allStepsCompleted && isLastStep) {
                // If this was the last step and all steps are completed, scroll after accordion fully closes
                setTimeout(() => {
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      this.scrollToBuyButton();
                    });
                  });
                }, 200);
              }
            }, 200);
          }
        } else {
          // Viewer info is not complete - unmark as completed if it was marked
          if (this.completedSteps.has(stepNumber)) {
            this.completedSteps.delete(stepNumber);
            accordion.classList.remove('stepped-accordion--completed');
            accordion.classList.add('stepped-accordion--active');
            
            // Hide checkmark but keep selection display
            const checkmark = accordion.querySelector('.stepped-accordion__checkmark');
            if (checkmark) {
              checkmark.style.display = 'none';
            }
            
            // Update add to cart button state
            this.updateAddToCartButton();
          }
        }
      }

      /**
       * Handle subscription type changes to show/hide add-ons step
       */
      handleSubscriptionTypeChange(detail) {
        const addonsAccordion = this.querySelector('.stepped-accordion--addons');
        if (!addonsAccordion) return;

        const subscriptionType = detail.type;
        const addonsStepNumber = this.accordions.indexOf(addonsAccordion) + 1;

        if (subscriptionType === 'existing' || subscriptionType === 'reseller' || subscriptionType === 'activate_later' || subscriptionType === null) {
          addonsAccordion.style.display = 'none';
          addonsAccordion.classList.add('stepped-accordion--skipped');
          addonsAccordion.classList.remove('stepped-accordion--locked', 'stepped-accordion--active');
        } else {
          addonsAccordion.style.display = '';
          addonsAccordion.classList.remove('stepped-accordion--skipped', 'stepped-accordion--locked', 'stepped-accordion--completed');
          addonsAccordion.classList.add('stepped-accordion--active');
          this.completedSteps.delete(addonsStepNumber);
          
          const checkmark = addonsAccordion.querySelector('.stepped-accordion__checkmark');
          if (checkmark) {
            checkmark.style.display = 'none';
          }
        }

        this.updateAddToCartButton();
      }


      /**
       * Handle a selection being made in a step
       */
      handleSelection(accordion, stepNumber, target) {
        const value = this.getSelectionValue(accordion, target);
        
        const isSubscription = accordion.classList.contains('stepped-accordion--subscription');
        const requiresViewerInfo = isSubscription && !this.checkSubscriptionViewerInfoComplete(accordion);
        
        if (isSubscription && this.completedSteps.has(stepNumber) && requiresViewerInfo) {
          this.completedSteps.delete(stepNumber);
          accordion.classList.remove('stepped-accordion--completed');
          accordion.classList.add('stepped-accordion--active');
          
          const selectionDisplay = accordion.querySelector('[data-selection-display]');
          const checkmark = accordion.querySelector('.stepped-accordion__checkmark');
          if (selectionDisplay) {
            selectionDisplay.innerHTML = '';
            selectionDisplay.style.display = 'none';
          }
          if (checkmark) {
            checkmark.style.display = 'none';
          }
        }
        
        if (requiresViewerInfo) {
          this.triggerVariantChange(target);
          this.updateAddToCartButton();
          return;
        }
        
        this.updateSelectionDisplay(accordion, value, true);
        
        this.markStepCompleted(accordion, stepNumber, true);
        this.completedSteps.add(stepNumber);
        this.triggerVariantChange(target);
        this.updateAddToCartButton();
        
        const allStepsCompleted = this.areAllStepsCompleted();
        const isLastStep = stepNumber === this.totalSteps;
        
        setTimeout(() => {
          this.closeStep(stepNumber);
          
          if (stepNumber < this.totalSteps) {
            this.openStep(stepNumber + 1);
            this.currentStep = stepNumber + 1;
          } else if (allStepsCompleted && isLastStep) {
            setTimeout(() => {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  this.scrollToBuyButton();
                });
              });
            }, 500);
          }
        }, 350);
      }

      /**
       * Trigger variant change event to update price
       */
      triggerVariantChange(target) {
        // Get all selected option values from all accordions
        const selectedOptionValues = [];
        
        this.accordions.forEach((accordion) => {
          // Check for radio buttons
          const checkedRadio = accordion.querySelector('input[type="radio"]:checked');
          if (checkedRadio && checkedRadio.dataset.optionValueId) {
            selectedOptionValues.push(checkedRadio.dataset.optionValueId);
          }
          
          // Check for select elements
          const select = accordion.querySelector('select');
          if (select && select.selectedIndex > 0) {
            const selectedOption = select.options[select.selectedIndex];
            if (selectedOption && selectedOption.dataset.optionValueId) {
              selectedOptionValues.push(selectedOption.dataset.optionValueId);
            }
          }
        });

        // Create a synthetic change event-like object
        // We can't modify Event.target (it's read-only), so create a plain object
        const changeEvent = {
          type: 'change',
          bubbles: true,
          target: target,
          currentTarget: target,
        };

        // Publish the option value selection change event
        // This will trigger product-info.js to update the price
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event: changeEvent,
              target: target,
              selectedOptionValues: selectedOptionValues,
            },
          });
        }
      }

      /**
       * Get the display value from the selection
       */
      getSelectionValue(accordion, target) {
        if (target.type === 'radio') {
          // For radio buttons (including subscription radios), get the label text
          const label = accordion.querySelector(`label[for="${target.id}"]`);
          if (label) {
            const labelClone = label.cloneNode(true);
            const hiddenElements = labelClone.querySelectorAll('.visually-hidden, .label-unavailable');
            hiddenElements.forEach(el => el.remove());
            return labelClone.textContent.trim();
          }
          return target.value;
        } else if (target.tagName === 'SELECT') {
          return target.options[target.selectedIndex].textContent.trim();
        }
        return '';
      }

      /**
       * Update the selection display in the accordion title
       */
      updateSelectionDisplay(accordion, value, showCheckmark = true) {
        const selectionDisplay = accordion.querySelector('[data-selection-display]');
        const checkmark = accordion.querySelector('.stepped-accordion__checkmark');
        
        if (selectionDisplay && value) {
          selectionDisplay.innerHTML = value;
          selectionDisplay.style.display = 'block';
        }
        
        if (checkmark) {
          if (showCheckmark) {
            checkmark.style.display = 'inline-flex';
          } else {
            checkmark.style.display = 'none';
          }
        }
      }

      /**
       * Mark a step as completed
       */
      markStepCompleted(accordion, stepNumber, unlockNext = true) {
        accordion.classList.remove('stepped-accordion--active', 'stepped-accordion--locked');
        accordion.classList.add('stepped-accordion--completed');
        
        if (unlockNext && stepNumber < this.totalSteps) {
          const nextAccordion = this.accordions[stepNumber];
          if (nextAccordion) {
            nextAccordion.classList.remove('stepped-accordion--locked');
            nextAccordion.classList.add('stepped-accordion--active');
          }
        }
      }

      /**
       * Open a specific step
       */
      openStep(stepNumber) {
        const accordion = this.accordions[stepNumber - 1];
        if (!accordion) return;

        const details = accordion.querySelector('details');
        if (details) {
          details.setAttribute('open', '');
        }
        
        accordion.classList.remove('stepped-accordion--locked');
        accordion.classList.add('stepped-accordion--active');

        // Scroll to the accordion with smooth animation
        setTimeout(() => {
          accordion.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 100);
      }

      /**
       * Close a specific step
       */
      closeStep(stepNumber) {
        const accordion = this.accordions[stepNumber - 1];
        if (!accordion) return;

        const details = accordion.querySelector('details');
        if (details) {
          details.removeAttribute('open');
        }
      }

      /**
       * Show a message when trying to open a locked accordion
       */
      showLockedMessage(accordion, stepNumber) {
        const previousStepName = this.getPreviousStepName(stepNumber);
        
        // Remove any existing tooltip
        const existingTooltip = accordion.querySelector('.stepped-accordion__tooltip');
        if (existingTooltip) existingTooltip.remove();
        
        // Create a temporary tooltip
        const tooltip = document.createElement('div');
        tooltip.className = 'stepped-accordion__tooltip';
        tooltip.textContent = this.pleaseCompleteFirst.replace('[STEP]', previousStepName);
        
        accordion.appendChild(tooltip);
        
        // Remove tooltip after animation
        setTimeout(() => {
          tooltip.remove();
        }, 2500);
      }

      /**
       * Get the name of the previous step
       */
      getPreviousStepName(stepNumber) {
        if (stepNumber <= 1) return '';
        
        const previousAccordion = this.accordions[stepNumber - 2];
        if (previousAccordion) {
          const optionName = previousAccordion.querySelector('.stepped-accordion__option-name');
          return optionName ? optionName.textContent.trim() : `Step ${stepNumber - 1}`;
        }
        return `Step ${stepNumber - 1}`;
      }

      /**
       * Reset all accordions to initial state
       */
      reset() {
        // Clear all selections
        this.clearPreSelectedOptions();
        
        // Reset completed steps tracking
        this.completedSteps.clear();
        
        this.accordions.forEach((accordion, index) => {
          const stepNumber = index + 1;
          const details = accordion.querySelector('details');
          const selectionDisplay = accordion.querySelector('[data-selection-display]');
          const checkmark = accordion.querySelector('.stepped-accordion__checkmark');
          
          // Reset classes
          if (stepNumber === 1) {
            accordion.classList.add('stepped-accordion--active');
            accordion.classList.remove('stepped-accordion--locked', 'stepped-accordion--completed');
            if (details) details.setAttribute('open', '');
          } else {
            accordion.classList.add('stepped-accordion--locked');
            accordion.classList.remove('stepped-accordion--active', 'stepped-accordion--completed');
            if (details) details.removeAttribute('open');
          }
          
          // Reset selection display
          if (selectionDisplay) {
            selectionDisplay.innerHTML = '';
            selectionDisplay.style.display = 'none';
          }
          
          // Hide checkmark
          if (checkmark) {
            checkmark.style.display = 'none';
          }
        });
        
        this.currentStep = 1;
        
        // Trigger variant change to reset price to default variant
        // Since all selections are cleared, this will use the first available variant
        this.triggerVariantChangeReset();
        
        // Update button state
        this.updateAddToCartButton();
      }

      /**
       * Trigger variant change event to reset price to default variant
       */
      triggerVariantChangeReset() {
        // Get the default/first available variant from the variant-selects element
        const variantSelects = this.querySelector('variant-selects');
        if (!variantSelects) return;

        const variantScript = variantSelects.querySelector('script[data-selected-variant]');
        if (!variantScript) return;

        try {
          const defaultVariant = JSON.parse(variantScript.textContent);
          
          // Get the default variant's option values by finding and selecting matching inputs
          const selectedOptionValues = [];
          const selectedTargets = [];
          
          if (defaultVariant.options && defaultVariant.options.length > 0) {
            defaultVariant.options.forEach((optValue, optIndex) => {
              const accordion = this.accordions[optIndex];
              if (!accordion) return;
              
              // Find the input/option that matches this default value
              const allInputs = accordion.querySelectorAll('input[type="radio"], select option');
              for (const input of allInputs) {
                const value = input.tagName === 'OPTION' ? input.value : input.value;
                // Match by exact value
                if (value === optValue || value.trim() === optValue.trim()) {
                  if (input.tagName === 'OPTION') {
                    // For select options, set the select's selectedIndex
                    const select = input.closest('select');
                    if (select) {
                      const optionIndex = Array.from(select.options).indexOf(input);
                      if (optionIndex >= 0) {
                        select.selectedIndex = optionIndex;
                        selectedTargets.push(select);
                        if (input.dataset.optionValueId) {
                          selectedOptionValues.push(input.dataset.optionValueId);
                        }
                      }
                    }
                  } else {
                    // For radio buttons, check it
                    input.checked = true;
                    selectedTargets.push(input);
                    if (input.dataset.optionValueId) {
                      selectedOptionValues.push(input.dataset.optionValueId);
                    }
                  }
                  break;
                }
              }
            });
          }

          // Trigger the variant change event using the last selected target
          // Use a small delay to ensure DOM updates are complete
          setTimeout(() => {
            if (selectedTargets.length > 0 && typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
              // Use the last selected target (usually the last variant option)
              const lastSelectedTarget = selectedTargets[selectedTargets.length - 1];
              
              // First, trigger native change events on all selected targets
              // This ensures variant-selects and other listeners pick them up
              selectedTargets.forEach((target) => {
                const nativeChangeEvent = new Event('change', { bubbles: true });
                target.dispatchEvent(nativeChangeEvent);
              });

              // Also publish the option value selection change event to reset price
              // Create a synthetic change event-like object
              const changeEvent = {
                type: 'change',
                bubbles: true,
                target: lastSelectedTarget,
                currentTarget: lastSelectedTarget,
              };

              publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
                data: {
                  event: changeEvent,
                  target: lastSelectedTarget,
                  selectedOptionValues: selectedOptionValues,
                },
              });
            }
          }, 100);
        } catch (e) {
          console.warn('Could not parse default variant for price reset:', e);
        }
      }
    }
  );
}

/**
 * Keep the old custom element name for backwards compatibility
 * but have it do nothing (the new stepped-product-options handles everything)
 */
if (!customElements.get('stepped-variant-accordion')) {
  customElements.define(
    'stepped-variant-accordion',
    class SteppedVariantAccordion extends HTMLElement {
      // Empty - functionality moved to stepped-product-options
    }
  );
}
